/**
 * Layout principal avec barre de navigation, toggle equipe et deconnexion.
 * Sous 768px, la navigation bascule dans un menu escamotable (voir Layout.css).
 */

import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTeamView } from "../contexts/TeamViewContext";
import "./Layout.css";

export default function Layout() {
  const { user, logoutUser } = useAuth();
  const { teamView, setTeamView } = useTeamView();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const canSeeTeam = user?.is_squad_lead || user?.is_practice_lead;

  // Referme le menu a chaque changement de page
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Echap referme le menu
  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  function handleLogout() {
    setMenuOpen(false);
    logoutUser();
    setTeamView(false);
    navigate("/login");
  }

  function handlePersoClick() {
    if (teamView) {
      setTeamView(false);
    }
    setMenuOpen(false);
  }

  function handleTeamClick() {
    setTeamView(true);
    setMenuOpen(false);
  }

  return (
    <div className="layout">
      <nav className="navbar">
        <div className="navbar-brand">
          <NavLink to="/contacts">
            <span className="brand-icon">&#x1F6F8;</span> UFO
          </NavLink>
          {teamView && (
            <span className="navbar-mode-badge">Équipe</span>
          )}
        </div>

        <button
          type="button"
          className="navbar-toggle"
          aria-expanded={menuOpen}
          aria-controls="navbar-menu"
          aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className={`burger${menuOpen ? " burger-open" : ""}`} />
        </button>

        <div
          id="navbar-menu"
          className={`navbar-menu${menuOpen ? " navbar-menu-open" : ""}`}
        >
          <div className="navbar-links">
            <NavLink to="/contacts" className={({ isActive }) => `nav-btn${isActive ? " nav-btn-active" : ""}`}>
              Contacts
            </NavLink>
            <NavLink to="/todos" className={({ isActive }) => `nav-btn${isActive ? " nav-btn-active" : ""}`}>
              To Dos
            </NavLink>
            <NavLink to="/actions" className={({ isActive }) => `nav-btn${isActive ? " nav-btn-active" : ""}`}>
              Réalisé
            </NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin/users" className={({ isActive }) => `nav-btn${isActive ? " nav-btn-active" : ""}`}>
                Admin
              </NavLink>
            )}
          </div>
          <div className="navbar-user">
            {user && (
              <span className="navbar-username" title={user.email}>
                {`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
              </span>
            )}
            <button
              className={`btn-view ${!teamView ? "active" : ""}`}
              onClick={handlePersoClick}
            >
              Vue perso
            </button>
            {canSeeTeam && (
              <button
                className={`btn-view ${teamView ? "active" : ""}`}
                onClick={handleTeamClick}
              >
                Vue équipe
              </button>
            )}
            <button onClick={handleLogout} className="btn-logout">
              Déconnexion
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div
          className="navbar-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
